// Broadcast overlay style presets (PRO feature). Every preset only changes
// colors and chrome — layout and stats stay identical so nothing can clip.
import { get } from 'svelte/store';
import { appSettings } from '../store.js';
import { currentUser } from '../svelte/store.js';
import { hasProAccess } from '../entitlements.js';

export const DEFAULT_OVERLAY_TITLE = 'FFPL S3';
export const DEFAULT_OVERLAY_LOGO = 'FFPL_Watermark_150x150.png';

// Each preset has a DISTINCT visual identity so PRO users see a real
// difference: unique background hue family, corner shape (radius), opacity,
// accent colour, and chrome (team panel / top line / shimmer / phase badge).
export const OVERLAY_STYLES = {
  // Warm charcoal + signature FFPL gold, full broadcast chrome, medium round.
  classic: {
    label: 'Classic (FFPL Gold)',
    barTop: 'rgba(18,14,8,0.94)', barBottom: 'rgba(6,4,2,0.98)',
    vertBg: 'rgba(18,14,8,0.90)',
    accent: '#fbbf24', radius: 10,
    shimmer: true, phaseBadge: true, teamPanel: true, topLine: true
  },
  // Airy, near-transparent glass. White accent, zero chrome, big soft corners.
  minimal: {
    label: 'Minimalist (Clean Glass)',
    barTop: 'rgba(20,22,28,0.42)', barBottom: 'rgba(14,16,20,0.52)',
    vertBg: 'rgba(18,20,26,0.44)',
    accent: '#f1f5f9', radius: 22,
    shimmer: false, phaseBadge: false, teamPanel: false, topLine: false
  },
  // Solid OPAQUE royal navy + gold, SHARP square corners. Corporate broadcast.
  professional: {
    label: 'Professional (Navy & Gold)',
    barTop: 'rgba(12,22,52,1)', barBottom: 'rgba(6,12,32,1)',
    vertBg: 'rgba(12,22,52,1)',
    accent: '#e3c96b', radius: 2,
    shimmer: false, phaseBadge: true, teamPanel: false, topLine: true
  },
  // Deep PURPLE/magenta + neon cyan, glossy, extra-round, full shimmer.
  vibrant: {
    label: 'Vibrant (Neon Purple)',
    barTop: 'rgba(48,10,72,0.90)', barBottom: 'rgba(20,4,44,0.96)',
    vertBg: 'rgba(48,10,72,0.88)',
    accent: '#22d3ee', radius: 18,
    shimmer: true, phaseBadge: true, teamPanel: true, topLine: true
  },
  // Pure BLACK, ultra-transparent stealth + lime pop, minimal chrome.
  night: {
    label: 'Night (Stealth Lime)',
    barTop: 'rgba(0,0,0,0.50)', barBottom: 'rgba(0,0,0,0.68)',
    vertBg: 'rgba(0,0,0,0.58)',
    accent: '#a3e635', radius: 8,
    shimmer: false, phaseBadge: false, teamPanel: false, topLine: true
  }
};

export function isProUser() {
  try { return hasProAccess(get(currentUser)); } catch (_) { return hasProAccess(null); }
}

// Free users always render the Classic FFPL look — customization is the PRO perk.
export function getOverlayTheme() {
  if (!isProUser()) return OVERLAY_STYLES.classic;
  return OVERLAY_STYLES[appSettings.overlayStyle] || OVERLAY_STYLES.classic;
}

export function getOverlayTitle() {
  if (!isProUser()) return DEFAULT_OVERLAY_TITLE;
  const custom = String(appSettings.overlayTitle ?? '').trim();
  return custom === '' ? DEFAULT_OVERLAY_TITLE : custom.slice(0, 22);
}

// Returns the image src for the watermark, or null when hidden.
export function getOverlayLogoSrc() {
  if (!isProUser()) return DEFAULT_OVERLAY_LOGO;
  const value = appSettings.overlayLogo;
  if (value === 'off') return null;
  if (typeof value === 'string' && value.startsWith('data:image/')) return value;
  return DEFAULT_OVERLAY_LOGO;
}
